import { useCallback, useEffect, useState } from "react";
import { Loader2, Mail, RotateCcw, ShieldCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, apiError, fmtWhen } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ContactsDownload } from "@/components/ContactsDownload";

const STATUS_WORDS = {
  invited: "invited, not signed in yet",
  active: "signed in",
  pending_approval: "waiting for SDTD's approval",
  refused: "turned down",
  deactivated: "no access",
};

const Kind = ({ on, label, testId, onClick, disabled }) => (
  <button
    type="button"
    data-testid={testId}
    disabled={disabled}
    onClick={onClick}
    className={`font-display border px-2 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors duration-200 disabled:opacity-40 ${
      on
        ? "border-primary text-primary"
        : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
    }`}
  >
    {label}
  </button>
);

/** One person: do their submissions wait for the SPOC, and for which kinds. */
const PersonRow = ({ row, kinds, plants, busy, canEdit, onSave, onReset, onInvite, onUnits }) => {
  const on = row.verification_required;
  const picked = row.kinds || [];
  const held = row.units || [];
  const unitRole = (row.role === "spoc" || row.role === "spoc_member") && canEdit;
  const toggleKind = (key) => {
    const next = picked.includes(key) ? picked.filter((k) => k !== key) : [...picked, key];
    if (!next.length) onSave(row, false, []);
    else onSave(row, true, next);
  };
  const toggleUnit = (code) => {
    const next = held.includes(code) ? held.filter((c) => c !== code) : [...held, code];
    if (!next.length) {
      toast.error(`${row.name} must hold at least one unit.`);
      return;
    }
    onUnits(row, next);
  };
  return (
    <div
      className="grid gap-3 border-t border-border px-3 py-3 lg:grid-cols-[1.6fr_1fr_1.4fr_auto] lg:items-center"
      data-testid={`approver-row-${row.email}`}
    >
      <div>
        <p className="text-sm font-semibold">{row.name || "—"}</p>
        <p className="font-data text-[11px] text-muted-foreground">{row.email}</p>
        {(row.mobile || row.whatsapp) && (
          <p className="font-data text-[11px] text-muted-foreground" data-testid={`approver-phone-${row.email}`}>
            {row.mobile && `Mobile ${row.mobile}`}
            {row.whatsapp && row.whatsapp !== row.mobile && `${row.mobile ? " · " : ""}WhatsApp ${row.whatsapp}`}
          </p>
        )}
        <p className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
          {row.role_label}
          {held.length ? ` · ${held.join(", ")}` : row.plant ? ` · ${row.plant}` : ""} ·{" "}
          {STATUS_WORDS[row.status] || row.status}
        </p>
        {unitRole && (
          <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`approver-units-${row.email}`}>
            {(plants || []).map((code) => (
              <button
                key={code}
                type="button"
                data-testid={`approver-unit-${row.email}-${code}`}
                disabled={busy}
                title={
                  held.includes(code)
                    ? `${row.name} looks after ${code}`
                    : `Give ${code} to ${row.name} as well`
                }
                onClick={() => toggleUnit(code)}
                className={`font-data border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider transition-colors duration-200 disabled:opacity-40 ${
                  held.includes(code)
                    ? "border-primary text-primary"
                    : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
                }`}
              >
                {code}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Switch
          checked={on}
          data-testid={`approver-switch-${row.email}`}
          onCheckedChange={(want) => onSave(row, want, want ? kinds.map((k) => k.key) : [])}
          disabled={busy || !canEdit}
        />
        <span className="text-[11px] leading-snug">
          {on ? "Verification required" : "Saves on submission"}
          <span className="block text-[10px] text-muted-foreground">
            {row.source === "set"
              ? `set by ${row.set_by || "SDTD"}${row.set_on ? ` · ${fmtWhen(row.set_on)}` : ""}`
              : "portal default"}
          </span>
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {kinds.map((k) => (
          <Kind
            key={k.key}
            on={on && picked.includes(k.key)}
            label={k.label}
            disabled={busy || !on || !canEdit}
            testId={`approver-kind-${row.email}-${k.key}`}
            onClick={() => toggleKind(k.key)}
          />
        ))}
        {!on && (
          <span className="text-[10px] text-muted-foreground">
            nothing waits — every submission goes straight into the register
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {canEdit && row.status === "invited" && (
          <Button
            variant="outline"
            className="h-8 rounded-none text-[10px] font-bold uppercase tracking-widest"
            data-testid={`approver-invite-${row.email}`}
            disabled={busy}
            onClick={() => onInvite(row)}
          >
            <Mail className="mr-1.5 h-3 w-3" /> Invite
          </Button>
        )}
        {canEdit && row.source === "set" && (
          <Button
            variant="ghost"
            className="h-8 rounded-none text-[10px] font-bold uppercase tracking-widest"
            data-testid={`approver-reset-${row.email}`}
            disabled={busy}
            onClick={() => onReset(row)}
          >
            <RotateCcw className="mr-1.5 h-3 w-3" /> Default
          </Button>
        )}
      </div>
    </div>
  );
};

export default function Approvers() {
  const { shows, scopedPlant } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState({ name: "", email: "", role: "spoc", plant: "", invite: true });

  const load = useCallback(() => {
    api
      .get("/approvers")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(load, [load]);

  const save = async (row, required, kinds) => {
    setBusy(true);
    try {
      const r = await api.put(`/approvers/${row.id}`, {
        verification_required: required,
        kinds,
      });
      setData(r.data);
      toast.success(
        required
          ? `${row.name}: ${kinds.length} kind(s) now wait for verification`
          : `${row.name}: submissions now save straight away`,
      );
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = async (row) => {
    setBusy(true);
    try {
      const r = await api.delete(`/approvers/${row.id}`);
      setData(r.data);
      toast.success(`${row.name} is back on the portal default`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setUnits = async (row, units) => {
    setBusy(true);
    try {
      const r = await api.put(`/approvers/${row.id}/units`, { units });
      setData(r.data);
      toast.success(`${row.name} now looks after ${units.join(", ")}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const invite = async (row) => {    setBusy(true);
    try {
      const r = await api.post(`/approvers/${row.id}/invite`);
      setData(r.data);
      toast[r.data.email_sent ? "success" : "error"](
        r.data.email_sent
          ? `Invitation sent to ${row.email}`
          : `The invitation could not go out: ${r.data.email_error || "mail refused"}`,
      );
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addPerson = async () => {
    setBusy(true);
    try {
      const r = await api.post("/approvers", adding);
      setData(r.data);
      setAdding({ name: "", email: "", role: adding.role, plant: adding.plant, invite: true });
      toast.success(
        adding.invite && r.data.email_sent
          ? `${adding.name} added and invited`
          : `${adding.name} added to the portal`,
      );
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );

  const unitRoles = (data.roles || []).filter((r) => r.key === "spoc" || r.key === "spoc_member");
  const needsUnit = adding.role === "spoc" || adding.role === "spoc_member";
  const canEdit = data.can_edit === true;
  const rowProps = {
    kinds: data.kinds || [],
    plants: data.plants || [],
    busy,
    canEdit,
    onSave: save,
    onReset: reset,
    onInvite: invite,
    onUnits: setUnits,
  };

  return (
    <div className="p-5 sm:p-8" data-testid="approvers-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps" data-testid="approvers-mode">
            {canEdit ? "SDTD super-admin" : "View only"}
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">Approvers</h1>
        </div>
        {shows("approvers.download") && (
          <ContactsDownload
            path="/approvers/sheet"
            units={(data.plants || []).map((p) => ({ code: p, name: data.plant_names?.[p] || p }))}
            current={scopedPlant || ""}
            name="SAIL_Approvers_Contacts"
            note="Every SPOC, designated member and partner with their e-mail, mobile, the units they hold and what waits for verification — for one unit, or for all units."
            testId="approvers-contacts"
          />
        )}
      </div>
      {canEdit ? (
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Who clears what, unit by unit. Switch <strong>Verification Required for Submission by this
          user</strong> on and what that officer files waits for their unit SPOC — a Pravartanam
          partner's work waits for the Partner Manager — before the register carries it. Switch it
          off and it saves on submission. Only the kinds you tick wait; the rest go straight in.
        </p>
      ) : (
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground" data-testid="approvers-view-note">
          Who clears what, unit by unit, for every unit of SAIL. Where <strong>Verification
          required</strong> shows, that officer&apos;s submissions of the kinds marked wait for their
          unit SPOC — a Pravartanam partner&apos;s for the Partner Manager — before the register
          carries them; otherwise they save on submission. Only SDTD&apos;s super-admins change this.
        </p>
      )}

      {canEdit && shows("approvers.add") && (
      <div className="mt-6 border border-border bg-card" data-testid="approvers-add">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <UserPlus className="h-4 w-4 text-primary" />
          <span className="label-caps">Add a SPOC, a designated member or a partner</span>
        </div>
        <div className="grid gap-3 p-3 lg:grid-cols-5 lg:items-end">
          <div>
            <label className="label-caps">Name</label>
            <Input
              className="mt-1 h-11 rounded-none"
              data-testid="approver-add-name"
              value={adding.name}
              onChange={(e) => setAdding((a) => ({ ...a, name: e.target.value }))}
            />
          </div>
          <div>
            <label className="label-caps">Email</label>
            <Input
              className="mt-1 h-11 rounded-none"
              data-testid="approver-add-email"
              placeholder="name@sail.in"
              value={adding.email}
              onChange={(e) => setAdding((a) => ({ ...a, email: e.target.value }))}
            />
          </div>
          <div>
            <label className="label-caps">Role</label>
            <Select
              value={adding.role}
              onValueChange={(v) => setAdding((a) => ({ ...a, role: v }))}
            >
              <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="approver-add-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-popover">
                {(data.roles || []).map((r) => (
                  <SelectItem key={r.key} value={r.key}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="label-caps">Unit</label>
            <Select
              value={adding.plant}
              onValueChange={(v) => setAdding((a) => ({ ...a, plant: v }))}
              disabled={!needsUnit}
            >
              <SelectTrigger className="mt-1 h-11 rounded-none" data-testid="approver-add-plant">
                <SelectValue placeholder={needsUnit ? "Choose" : "not for a partner"} />
              </SelectTrigger>
              <SelectContent className="max-h-72 rounded-none bg-popover">
                {(data.plants || []).map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch
                checked={adding.invite}
                data-testid="approver-add-invite"
                onCheckedChange={(v) => setAdding((a) => ({ ...a, invite: v }))}
              />
              <span className="text-[11px]">Send the invitation now</span>
            </div>
            <Button
              className="h-11 rounded-none text-[11px] font-bold uppercase tracking-widest"
              data-testid="approver-add-submit"
              disabled={busy || !adding.name.trim() || !adding.email.trim()}
              onClick={addPerson}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
            </Button>
          </div>
        </div>
        <p className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
          Unit roles need a unit, and may hold several — press the unit codes under a SPOC's name to
          give or take one away. The invitation carries its own passcode, so the officer signs in
          straight from that mail; leave it off to add the name now and invite later.
        </p>
      </div>
      )}

      {(data.units || []).map((u) => (
        <div key={u.plant} className="mt-6 border border-border bg-card" data-testid={`approvers-unit-${u.plant}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2">
            <div className="flex items-baseline gap-3">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <span className="font-display text-sm font-bold tracking-tight">{u.plant}</span>
              <span className="text-[11px] text-muted-foreground">
                {u.spoc ? `SPOC · ${u.spoc}` : "no SPOC on the portal yet"}
              </span>
            </div>
            <span className="font-data text-[11px] text-muted-foreground">
              {u.rows.length} person(s)
            </span>
          </div>
          {u.rows.length === 0 ? (
            <p className="border-t border-border px-3 py-3 text-[11px] text-muted-foreground">
              Nobody from this unit is on the portal yet{canEdit ? " — add their SPOC above." : "."}
            </p>
          ) : (
            u.rows.map((r) => (
              <PersonRow
                key={r.id}
                row={r}
                {...rowProps}
              />
            ))
          )}
        </div>
      ))}

      <div className="mt-6 border border-border bg-card" data-testid="approvers-partners">
        <div className="flex items-baseline gap-3 px-3 py-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <span className="font-display text-sm font-bold tracking-tight">
            Pravartanam partners
          </span>
          <span className="text-[11px] text-muted-foreground">
            a partner's work is cleared by the Partner Manager
          </span>
        </div>
        {(data.partners || []).length === 0 ? (
          <p className="border-t border-border px-3 py-3 text-[11px] text-muted-foreground">
            No partner account on the portal yet.
          </p>
        ) : (
          data.partners.map((r) => (
            <PersonRow
              key={r.id}
              row={r}
              {...rowProps}
            />
          ))
        )}
      </div>

      {!!(data.unplaced || []).length && (
        <div className="mt-6 border border-border bg-card" data-testid="approvers-unplaced">
          <div className="px-3 py-2">
            <span className="label-caps">No unit against the account</span>
          </div>
          {data.unplaced.map((r) => (
            <PersonRow
              key={r.id}
              row={r}
              {...rowProps}
            />
          ))}
        </div>
      )}

      <p className="mt-6 max-w-3xl text-[11px] text-muted-foreground">
        The portal's own default: a unit's SPOC and the Pravartanam Partner Manager clear other
        people's work, so theirs goes straight in; everyone else's waits. Where you ask for a
        SPOC's own work to be verified, SDTD is the only one above them, so it waits for an SDTD
        administrator. Unit roles with no unit SPOC on the portal save straight away unless you
        have set a rule here. {unitRoles.length ? "" : ""}
      </p>
    </div>
  );
}
